import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EastIcon from '@mui/icons-material/East';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigationIcon from '@mui/icons-material/Navigation';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form3800SchA = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Schedule A (Form 3800) | TaxZerone Guide"
          description="Prepare and file Schedule A (Form 3800) correctly. Learn transfer rules, attachment requirements, and how exempt organizations report credits with Form 990-T."
          keywords=""
          canonical="resources/tax-exempt/form-3800-schedule-a/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Schedule A{' '}
          <span className="highlight-container">
            <span className="highlight">(Form 3800) </span>
          </span>{' '}
          - Transfer Election Statement
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Accurately report transferred credits and stay IRS-compliant
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f3800scha`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-3800-schedule-a/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 3800 Schedule A at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Schedule A (Form 3800)? Schedule A (Form
                          3800){' '}
                        </strong>{' '}
                        is used as a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          transfer election statement{' '}
                        </strong>
                        to report the required information when eligible general
                        business credits are transferred under{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Section 6418.
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It? Credit transferors and transferees
                        </strong>{' '}
                        may use Schedule A to document a transferred eligible
                        credit and attach the statement to their respective tax
                        returns.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule A reports the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          credit being transferred, transfer registration
                          number, payment amount and date,{' '}
                        </strong>
                        and other required information about the credit
                        transfer.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 3800 Connection:
                        </strong>{' '}
                        Schedule A works with{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-3800-business-credits/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 3800
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          , General Business Credit,
                        </strong>{' '}
                        and is attached to the applicable annual tax return when
                        a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Section 6418 transfer election{' '}
                        </strong>
                        is made.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Multiple Transfers:{' '}
                        </strong>
                        A separate Schedule A or equivalent transfer election
                        statement may be required for each transferred credit
                        transaction or applicable transfer.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule A of Form 3800, titled{' '}
                  <span style={{ fontWeight: 600 }}>
                    Transfer Election Statement,
                  </span>{' '}
                  must be completed when a business or exempt organization
                  transfers an eligible tax credit to another taxpayer. Use this
                  schedule to officially report the credit transfer and provide
                  the required details about both parties involved in the
                  transaction. The form records the name and identification
                  information of the transferor and transferee, identifies the
                  specific credit being transferred, and shows the exact credit
                  amount for the tax year. The IRS requires this information to
                  clearly document the transfer and ensure the same credit is
                  not claimed more than once.{' '}
                  <span style={{ fontWeight: 600 }}>
                    If an exempt organization makes or receives a credit
                    transfer, attach Schedule A (Form 3800) to{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-T
                    </Link>{' '}
                    when filing the return.
                  </span>{' '}
                  This attachment confirms that the credit transfer election has
                  been properly reported to the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For exempt filers, this form is especially important. Some
                  credits may not always be fully usable by exempt
                  organizations, so transferring the credit can be a good idea.
                  Schedule A makes sure that the transfer is properly reported,
                  can be tracked, and is accepted by the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This form does <span style={{ fontWeight: 600 }}>not</span>{' '}
                  calculate the credit, change its value, or apply tax limits.
                  Instead, it serves as formal documentation that connects the
                  transferred credit back to the original credit calculation and
                  confirms that both parties are reporting the transfer
                  consistently. A completed Schedule A (Form 3800) must be{' '}
                  <span style={{ fontWeight: 600 }}>
                    attached to the tax return of both the transferor and the
                    transferee.
                  </span>{' '}
                  Without it, the IRS will not recognize the credit
                  transfer-even if the underlying credit itself is valid.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This page explains{' '}
                  <span style={{ fontWeight: 600 }}>
                    every part of Schedule A (Form 3800)
                  </span>{' '}
                  in detail, with a focus on how exempt filers use it and what
                  information the IRS expects to see. For readers who want
                  additional background on the broader rules and framework
                  behind credit transfers, our{' '}
                  <span style={{ fontWeight: 600 }}>
                    Transfer Election Statement resource page
                  </span>{' '}
                  provides supplemental guidance.
                </Typography>
                <Alert
                  severity="info"
                  icon={<ErrorOutlineIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{fontWeight:600}} >important:</strong> This credit was transferred from one
                  taxpayer to another, and both parties agree to the transfer.
                </Alert> */}
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents{' '}
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Schedule A (Form 3800)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule A (Form 3800)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#credit" sx={{ color: '#0202ff' }}>
                          What credits can be Transferred?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Schedule A (Form 3800) with Taxzerone
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#source" sx={{ color: '#0202ff' }}>
                          Source and References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is Schedule A (Form 3800)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule A (Form 3800), also called the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Transfer Election Statement,
                  </span>{' '}
                  is the IRS form used to formally report that an eligible tax
                  credit was{' '}
                  <span style={{ fontWeight: 600 }}>
                    transferred from one taxpayer to another.
                  </span>{' '}
                  It serves as the official confirmation that{' '}
                  <span style={{ fontWeight: 600 }}>
                    both parties agreed to the transfer.
                  </span>{' '}
                  Without this statement attached to the return, the IRS will
                  not recognize the transferred credit.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Taxzerone provides end-to-end guidance on the{' '}
                  <Link
                    href="/resources/tax-exempt/transfer-election-statement/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Transfer Election Statement
                  </Link>
                  . Refer to the instructions for a clear explanation of credit
                  transfers and filing requirements.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Schedule A (Form 3800)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Schedule A must be completed and attached by{' '}
                  <span style={{ fontWeight: 600 }}>both parties</span>
                  involved in the credit transfer:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 2,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}> Transferor –</span> the
                      exempt organization or taxpayer that earned the credit and
                      transferred it
                    </Box>
                  </li>

                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}> Transferee -</span> the
                      taxpayer that received the credit and will claim it
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Each party attaches Schedule A to their own tax return for the
                  year of transfer.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="credit"
                >
                  What credits can be Transferred?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Only{' '}
                  <span style={{ fontWeight: 600 }}> eligible credits</span> may
                  be transferred. These commonly include certain clean energy,
                  investment, and other legislatively approved credits. The
                  specific credit must be calculated on its{' '}
                  <span style={{ fontWeight: 600 }}>
                    original IRS credit form
                  </span>{' '}
                  before it can be transferred.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Schedule A does not make a credit transferable; it only
                  reports a transfer that is already allowed by law. To learn
                  more about eligible credits and the related transfer
                  guidelines, refer to our{' '}
                  <Link
                    href="/resources/tax-exempt/transfer-election-statement/#what"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    TE Statement Credits
                  </Link>{' '}
                  resource page.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to file Schedule A (Form 3800) with Taxzerone
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-scha-img.webp" />
                  <source srcSet="/assets/images/f3800-scha-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-scha-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 3800) Transfer Election Statement showing fields for transferor and transferee names, TINs, and addresses."
                    width={760}
                    height={204}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Give the full legal name, TIN, and mailing address of both the
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    transferor (the person giving the credit)
                  </span>{' '}
                  and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    transferee (the person receiving the credit).
                  </span>{' '}
                  This makes sure that the IRS can keep track of the transfer
                  correctly.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-line1-img.webp" />
                  <source srcSet="/assets/images/f3800-line1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-line1-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 3800) lines 1–4 section to select credit form, enter IRS registration number, credit amount transferred, and payment date and amount."
                    width={760}
                    height={293}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To find out how much credit is being transferred, check the
                  box that matches the tax form you sent in.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the IRS transfer registration number for this credit
                  transaction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the IRS transfer registration number for this credit
                  transaction.
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 1,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      The amount that was reported as negative in Part III,
                      column (f) of the transferor's Form 3800, and
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      The amount that is positive in Part III, column (f) of the
                      transferee's Form 3800.{' '}
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Both sides must report the same amounts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the payment amount and the date of payment on Line 4a.
                  If more than one payment was made for this credit transfer,
                  report each additional payment on Lines 4b through 4h.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-line5-img.webp" />
                  <source srcSet="/assets/images/f3800-line5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-line5-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 3800) lines 5–12 section for entering credit amount, tax year end date, and certification questions with Yes/No boxes for transferor compliance."
                    width={760}
                    height={261}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the full amount of the credit earned by the transferor
                  for the credit type you selected on Line 1. Be sure this
                  amount matches the IRS transfer registration number entered on
                  Line 2 to ensure accurate reporting and avoid delays.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the transferor’s tax year ending date in MM/DD/YYYY
                  format. Make sure the date matches the tax year for which the
                  credit was earned.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm whether the transferor (and any related controlled
                  group members) is not related to the transferee under IRS
                  rules (Sections 267(b) or 707(b)(1)). Select “Yes” only if
                  they are completely unrelated.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that the transferor has complied - or will comply -
                  with all requirements under Section 6418 and any IRS guidance
                  related to the transferred credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that the transferor has provided the transferee with
                  all required documentation as outlined in IRS Regulations
                  Section 1.6418-2(b)(5)(iv).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that the transferee (and any members of its controlled
                  group) is not related to the transferor under IRS
                  related-party rules (Sections 267(b) or 707(b)(1)).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the first tax year ending date (MM/DD/YYYY) in which the
                  transferee will claim or apply the transferred credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that both the transferor and transferee understand and
                  acknowledge the IRS recapture rules under Section 6418(g)(3),
                  if applicable.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-line13-img.webp" />
                  <source srcSet="/assets/images/f3800-line13-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-line13-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 3800) lines 13a–13b showing signature lines and certification statements for transferor and transferee on a Transfer Election Statement."
                    width={760}
                    height={207}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  File Schedule A (Form 3800) electronically, except for Lines
                  13a and 13b.Print the form, complete the signature section,
                  save it as a PDF named{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    “Schedule A Form 3800”,
                  </span>{' '}
                  and attach it to your return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 13a (Transferor)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Sign, date, and confirm that the information is correct and
                  that you have the power to approve the credit transfer.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 13b (Transferee)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Sign, date, and confirm that you have the right to do this and
                  agree to accept the credit.
                </Typography>

                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                  id="source"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This information is based on official IRS guidance.
                        Refer to the IRS Instructions for Form 3800 (General
                        Business Credit) and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/f3800a.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS Schedule A (Form 3800)
                        </Link>{' '}
                        – Transfer Election Statement for detailed filing
                        requirements and reporting rules.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Schedule A (Form 3800)?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              EEnsure your transferred business tax credits are properly
              documented and attached in compliance with IRS requirements.
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              TaxZerone is a safe and dependable way to fill out and send in
              Schedule A (Form 3800) – Transfer Election Statement,{' '}
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              as well as Form 3800 and Form 990-T for organizations that don't
              have to pay taxes.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f3800scha`}
              >
                E-file Form 990-T Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form3800SchA;
